import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AppIcon } from "@/components/AppIcon";
import { AREE, sectionOf, canOpen } from "@/components/shell/navConfig";
import { useOpenApp } from "@/components/shell/useOpenApp";
import { StrumentiMenu } from "@/components/shell/StrumentiMenu";

const Pannello = ({ area, apps, onOpen, onArea, right }) => (
  <div className={`absolute ${right ? "right-0" : "left-0"} top-full z-50 pt-2 whitespace-normal`} data-testid={`aree-panel-${area.key}`}>
    <div className="w-[640px] max-w-[calc(100vw-4rem)] rounded-2xl bg-white border border-border shadow-xl overflow-hidden" style={{ borderTop: `4px solid ${area.color}` }}>
      <div className="grid grid-cols-3">
        <div className="col-span-2 grid grid-cols-2 gap-1 p-4">
          {apps.map((a) => (
            <button key={a.app_id} onClick={() => onOpen(a)} data-testid={`aree-item-${a.app_id}`} className="flex items-start gap-3 rounded-lg p-2.5 text-left hover:bg-sage transition-colors">
              <span className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${area.color}22`, color: area.key === "direzionale" ? "#1B2380" : area.color }}><AppIcon name={a.icon} className="h-4 w-4" /></span>
              <span className="min-w-0"><span className="block text-sm font-medium text-navy">{a.name}</span><span className="block text-xs text-slate-500 line-clamp-2">{a.description}</span></span>
            </button>
          ))}
        </div>
        <div className="bg-navy text-white p-5 flex flex-col">
          <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "#F6C400" }}>In evidenza</p>
          <p className="font-heading text-lg mt-2">{area.label}</p>
          <p className="text-sm text-white/75 mt-1 flex-1">{area.desc || `${apps.length} funzioni abilitate per te.`}</p>
          <button onClick={onArea} data-testid={`aree-vai-${area.key}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#F6C400] hover:underline">Vai all'area <ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  </div>
);

export const AreeMenu = () => {
  const { user, apps } = useAuth();
  const [open, setOpen] = useState(null);
  const nav = useNavigate();
  const openApp = useOpenApp();
  const list = apps || [];
  const aree = AREE.map((a) => ({ ...a, apps: list.filter((x) => sectionOf(x) === a.key) })).filter((a) => a.apps.length);

  return (
    <nav className="hidden lg:flex h-12 items-center gap-0.5 px-8 border-t border-border whitespace-nowrap" data-testid="aree-menu" onMouseLeave={() => setOpen(null)}>
      {aree.map((a, idx) => (
        <div key={a.key} className="relative h-full flex items-center" onMouseEnter={() => setOpen(a.key)}>
          <button onClick={() => setOpen(open === a.key ? null : a.key)} data-testid={`aree-btn-${a.key}`} aria-expanded={open === a.key}
            className={`flex items-center gap-2 h-9 px-2.5 rounded-lg text-sm font-medium transition-colors ${open === a.key ? "bg-sage text-navy" : "text-slate-700 hover:text-navy"}`}>
            <span className="h-2.5 w-2.5 rounded-full ring-1 ring-black/5" style={{ backgroundColor: a.color }} /> <span className="min-[1440px]:hidden">{a.short}</span><span className="hidden min-[1440px]:inline">{a.label}</span> <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {open === a.key && <Pannello area={a} apps={a.apps} right={idx >= aree.length / 2} onOpen={(x) => { setOpen(null); openApp(x); }} onArea={() => { setOpen(null); nav(a.route); }} />}
        </div>
      ))}
      {canOpen(user, apps, "/trattative") && (
        <button onClick={() => nav("/trattative?nuova=1")} data-testid="aree-nuova-trattativa" className="ml-2 flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-semibold text-[#0B7770] hover:bg-sage transition-colors">
          <Plus className="h-4 w-4" /> Nuova<span className="hidden xl:inline ml-1">trattativa</span>
        </button>
      )}
      <StrumentiMenu />
    </nav>
  );
};
